import type { ObservationDefinition } from "@medplum/fhirtypes";
import { FINDING_DEFINITION_KIND, getTRPCClient, syncKey } from "@repo/api";
import {
  type ChangeFindingDefinitionAvailabilityInput,
  type CorrectFindingDefinitionInput,
  changeFindingDefinitionAvailabilityInputSchema,
  correctFindingDefinitionInputSchema,
  type DefineFindingDefinitionInput,
  defineFindingDefinitionInputSchema,
  type ReviseFindingDefinitionInput,
  reviseFindingDefinitionInputSchema,
} from "@repo/api/schemas";
import { type FindingComponent, mintEntityId } from "@repo/domain";
import { canonicalBase, canonicalUrl } from "@repo/fhir-systems";
import { and, eq, ilike, max, or, useLiveQuery } from "@tanstack/react-db";
import { defineWrite } from "../actions/define-write";
import { useCollection } from "../core/hooks";
import { getDataStoreClient } from "../core/provider";
import { deriveFindingDefinitionFields } from "../fhir/collections";
import { readFindingDefinitionRow, writeFindingDefinitionRow } from "../fhir/finding-definition-storage";
import type { FindingDefinitionRow } from "../fhir/types";
import { optimisticDefinedFinding, optimisticRevisedFinding } from "../lib/finding-definition-optimistic";
import { snapshotOfFinding } from "./definition-revision-snapshot";
import { buildVersionHistory, type DefinitionVersionHistoryDto } from "./definition-version-history";

const FINDING_DEFINITION_COLLECTION = "findingDefinition";

/** Every finding canonical this app mints sits directly under this base. */
export const FINDING_DEFINITION_CANONICAL_BASE = canonicalBase("ObservationDefinition");

export interface FindingOptionDto {
  optionId: string;
  label: string;
  /** What the option means, for a recorder unsure which to pick. */
  detail?: string;
  /** The finding's one normal result, if it has one. Exclusive on a multiple-choice finding. */
  normal?: true;
}

interface FindingDefinitionDtoBase {
  definitionUrl: string;
  name: string;
  description?: string;
  availability: "Active" | "Inactive";
  version: number;
  authoredAt: string;
}

/** UI read model for registry browse, registry authoring, and the exam editor's picker. */
export type FindingDefinitionDto = FindingDefinitionDtoBase &
  (
    | {
        kind: "Choice";
        cardinality: "Single" | "Multiple";
        offerOther: boolean;
        options: FindingOptionDto[];
      }
    | { kind: "Measured"; components: readonly FindingComponent[] }
  );

export interface FindingDefinitionFilters {
  availability?: FindingDefinitionDto["availability"];
  search?: string;
}

const questionnaires = () => getDataStoreClient().getCollection(FINDING_DEFINITION_COLLECTION);

/**
 * A finding is stored as exactly one item, so every read below is off `item[0]`
 * rather than a tree walk.
 */
export function projectFindingDefinition(resource: ObservationDefinition): FindingDefinitionDto {
  const facts = readFindingDefinitionRow(resource);
  const base: FindingDefinitionDtoBase = {
    definitionUrl: facts.spine.url,
    name: facts.spine.name,
    description: facts.spine.description,
    availability: facts.spine.availability,
    version: facts.spine.version,
    authoredAt: facts.spine.mintedAt,
  };
  if (facts.kind === "Measured") return { ...base, kind: "Measured", components: facts.components };
  return {
    ...base,
    kind: "Choice",
    cardinality: facts.cardinality,
    offerOther: facts.offerOther,
    options: facts.options.map((option) => ({
      optionId: option.id,
      label: option.label,
      ...(option.detail === undefined ? {} : { detail: option.detail }),
      ...(option.normal ? { normal: true as const } : {}),
    })),
  };
}

/** Mint once before dispatch so optimistic and authoritative writes share lineage identity. */
export function mintFindingDefinitionUrl(): string {
  return canonicalUrl("ObservationDefinition", mintEntityId());
}

/** Current revision of every registry finding matching the filters. */
export function useFindingDefinitions(filters: FindingDefinitionFilters = {}): FindingDefinitionDto[] {
  const collection = useCollection(FINDING_DEFINITION_COLLECTION);
  const status =
    filters.availability === undefined ? undefined : filters.availability === "Active" ? "active" : "retired";
  const search = filters.search?.trim();
  const result = useLiveQuery({
    queryKey: [collection.id, "finding-definitions", status ?? null, search ?? null],
    query: (query) => {
      const latestVersions = query
        .from({ candidate: collection })
        .where(({ candidate }) => eq(candidate.catalogModelKind, FINDING_DEFINITION_KIND))
        .groupBy(({ candidate }) => candidate.url)
        .select(({ candidate }) => ({ url: candidate.url, version: max(candidate.catalogVersion) }));
      const currentDefinitions = query
        .from({ definition: collection })
        .join({ latest: latestVersions }, ({ definition, latest }) => eq(definition.url, latest.url))
        .where(({ definition, latest }) =>
          and(eq(definition.catalogModelKind, FINDING_DEFINITION_KIND), eq(definition.catalogVersion, latest.version)),
        );
      const availableDefinitions = status
        ? currentDefinitions.where(({ definition }) => eq(definition.status, status))
        : currentDefinitions;
      const matchingDefinitions = search
        ? availableDefinitions.where(({ definition }) =>
            or(ilike(definition.title, `%${search}%`), ilike(definition.description, `%${search}%`)),
          )
        : availableDefinitions;
      return matchingDefinitions
        .orderBy(({ definition }) => definition.title, "asc")
        .fn.select(({ definition }) => projectFindingDefinition(definition));
    },
  });
  return (result.data as FindingDefinitionDto[] | undefined) ?? [];
}

/** Current revision of one registry finding lineage. */
export function useFindingDefinition(url: string | undefined): FindingDefinitionDto | undefined {
  const collection = useCollection(FINDING_DEFINITION_COLLECTION);
  const result = useLiveQuery({
    queryKey: [collection.id, "finding-definition", url ?? null],
    query: (query) => {
      if (!url) return undefined;
      return query
        .from({ definition: collection })
        .where(({ definition }) =>
          and(eq(definition.catalogModelKind, FINDING_DEFINITION_KIND), eq(definition.url, url)),
        )
        .orderBy(({ definition }) => definition.catalogVersion, "desc")
        .fn.select(({ definition }) => projectFindingDefinition(definition))
        .findOne();
    },
  });
  return result.data as unknown as FindingDefinitionDto | undefined;
}

/**
 * Every revision of one finding lineage, newest first. Its own hook rather than
 * a branch of `useDefinitionVersionHistory`: that query reads the
 * activityDefinition / planDefinition collections, and a finding is a
 * ObservationDefinition sharing one collection with exams and questionnaires, so the
 * lineage is found through `catalogModelKind` instead. The diffing is shared.
 */
export function useFindingDefinitionVersionHistory(
  definitionUrl: string | undefined,
): DefinitionVersionHistoryDto | undefined {
  const collection = useCollection(FINDING_DEFINITION_COLLECTION);
  const result = useLiveQuery({
    queryKey: [collection.id, "finding-definition-version-history", definitionUrl ?? null],
    query: (query) => {
      if (!definitionUrl) return undefined;
      return (
        query
          .from({ definition: collection })
          .where(({ definition }) =>
            and(eq(definition.catalogModelKind, FINDING_DEFINITION_KIND), eq(definition.url, definitionUrl)),
          )
          .orderBy(({ definition }) => definition.catalogVersion, "desc")
          // Options and flags live in extensions, which no declarative operator reaches.
          .fn.select(({ definition }) => ({
            resourceId: definition.id ?? "",
            author: definition.meta?.author?.display,
            dto: projectFindingDefinition(definition),
          }))
      );
    },
  });
  const revisions = result.data;
  if (!definitionUrl || !revisions?.length) return undefined;
  return buildVersionHistory(
    "finding",
    definitionUrl,
    revisions.map(({ resourceId, author, dto }) => ({
      resourceId,
      version: String(dto.version),
      authoredAt: dto.authoredAt,
      ...(author ? { author } : {}),
      status: dto.availability === "Active" ? "active" : "retired",
      snapshot: snapshotOfFinding(dto),
    })),
  );
}

function currentFindingResource(url: string): FindingDefinitionRow {
  let current: FindingDefinitionRow | undefined;
  for (const candidate of questionnaires().state.values()) {
    if (candidate.url !== url || candidate.catalogModelKind !== FINDING_DEFINITION_KIND) continue;
    if (!current || candidate.catalogVersion > current.catalogVersion) current = candidate;
  }
  if (!current?.id) throw new Error(`Finding Definition '${url}' is not available in the data store`);
  return current;
}

export const defineFindingDefinition = defineWrite<DefineFindingDefinitionInput>({
  optimistic: (params) => {
    const input = defineFindingDefinitionInputSchema.parse(params);
    questionnaires().insert(optimisticDefinedFinding(input, new Date().toISOString()));
  },
  persist: async (input, options) => {
    await getTRPCClient().catalog.findingDefinition.define.mutate(input, options);
  },
});

export const reviseFindingDefinition = defineWrite<ReviseFindingDefinitionInput>({
  optimistic: (params) => {
    const input = reviseFindingDefinitionInputSchema.parse(params);
    questionnaires().insert(
      optimisticRevisedFinding(currentFindingResource(input.definitionUrl), input, new Date().toISOString()),
    );
  },
  persist: async (input, options) => {
    await getTRPCClient().catalog.findingDefinition.revise.mutate(input, options);
  },
});

export const correctFindingDefinition = defineWrite<CorrectFindingDefinitionInput>({
  optimistic: (params) => {
    const input = correctFindingDefinitionInputSchema.parse(params);
    const resource = currentFindingResource(input.definitionUrl);
    questionnaires().update(syncKey(resource), (draft) => {
      const facts = readFindingDefinitionRow(resource);
      if (input.name !== undefined) facts.spine.name = input.name;
      if (input.description === null) delete facts.spine.description;
      else if (input.description !== undefined) facts.spine.description = input.description;
      const written = writeFindingDefinitionRow(facts, resource);
      Object.assign(draft, written, deriveFindingDefinitionFields(written));
    });
  },
  persist: async (input, options) => {
    await getTRPCClient().catalog.findingDefinition.correct.mutate(input, options);
  },
});

function availabilityAction(availability: "Active" | "Inactive") {
  return defineWrite<ChangeFindingDefinitionAvailabilityInput>({
    optimistic: (params) => {
      const input = changeFindingDefinitionAvailabilityInputSchema.parse(params);
      const resource = currentFindingResource(input.definitionUrl);
      questionnaires().update(syncKey(resource), (draft) => {
        const facts = readFindingDefinitionRow(resource);
        facts.spine.availability = availability;
        const written = writeFindingDefinitionRow(facts, resource);
        Object.assign(draft, written, deriveFindingDefinitionFields(written));
      });
    },
    persist: async (input, options) => {
      const mutation =
        availability === "Active"
          ? getTRPCClient().catalog.findingDefinition.activate
          : getTRPCClient().catalog.findingDefinition.deactivate;
      await mutation.mutate(input, options);
    },
  });
}

export const activateFindingDefinition = availabilityAction("Active");
export const deactivateFindingDefinition = availabilityAction("Inactive");
